import { forwardRef, useMemo } from 'react';
import type { InvoiceWithRelations } from '@/types/invoice';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { resolveTaxConfig } from '@/utils/tax';
import { DocumentSheet } from '@/components/documents/DocumentSheet';
import { buildInvoiceDocument } from '@/components/documents/adapters';
import { useSignedStorageUrl } from '@/hooks/useSignedStorageUrl';
import { useJobPayments } from '@/hooks/useJobPayments';
import { JOB_SIGNATURES_BUCKET } from '@/components/invoices/InvoiceSignatureDialog';

interface InvoicePreviewProps {
  invoice: InvoiceWithRelations;
  variant?: 'print' | 'screen';
}

/**
 * Printable A4 tax invoice. Thin wrapper: resolves tenant profile, tax snapshot
 * and locale, then hands a DocumentModel to the shared DocumentSheet renderer.
 */
export const InvoicePreview = forwardRef<HTMLDivElement, InvoicePreviewProps>(
  ({ invoice, variant = 'print' }, ref) => {
    const { data: company } = useCompanyProfile();
    const liveTax = useCompanyTax();
    const { locale, timezone } = useCompanyLocale();
    const { data: signatureImageUrl } = useSignedStorageUrl(JOB_SIGNATURES_BUCKET, invoice.signature_path);
    // The job's payments, so a part payment added on the job after the invoice was
    // raised still prints as "Partial payment" with the balance that is left.
    const { data: payments } = useJobPayments(invoice.job_id ?? null);

    const doc = useMemo(
      () => buildInvoiceDocument(invoice, {
        company,
        tax: resolveTaxConfig(invoice, liveTax),
        locale,
        timezone,
        signatureImageUrl: signatureImageUrl ?? null,
        payments: payments ?? null,
      }),
      [invoice, company, liveTax, locale, timezone, signatureImageUrl, payments],
    );

    return <DocumentSheet ref={ref} doc={doc} variant={variant} />;
  },
);

InvoicePreview.displayName = 'InvoicePreview';
